<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core'
  import type { VariantProps } from 'class-variance-authority'
  import {
    ToggleGroupItem,
    type ToggleGroupItemProps,
    useForwardProps
  } from 'reka-ui'
  import { type HTMLAttributes, inject } from 'vue'
  import type { IconComponent } from '@gat-ship/icons'
  import { cn } from '../../../lib/utils'
  import { toggleVariants } from '../toggle'

  type ToggleGroupVariants = VariantProps<typeof toggleVariants>

  const props = defineProps<
    ToggleGroupItemProps & {
      class?: HTMLAttributes['class']
      variant?: ToggleGroupVariants['variant']
      size?: ToggleGroupVariants['size']
      icon?: IconComponent
      label?: string
    }
  >()

  const context = inject<ToggleGroupVariants & { separated?: boolean }>(
    'toggleGroup'
  )

  const delegatedProps = reactiveOmit(props, 'class', 'size', 'variant')
  const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <ToggleGroupItem
    data-slot="toggle-group-item"
    :data-variant="context?.variant || variant"
    :data-size="context?.size || size"
    v-bind="forwardedProps"
    :class="
      cn(
        toggleVariants({
          variant: context?.variant || variant,
          size: context?.size || size
        }),
        'flex shrink-0 shadow-none focus:z-10 focus-visible:z-10 data-[state=on]:z-10',
        context?.separated
          ? 'rounded-md'
          : 'rounded-none first:rounded-l-md last:rounded-r-md data-[variant=outline]:-ml-px data-[variant=outline]:first:ml-0',
        props.class
      )
    "
  >
    <slot>
      <div class="flex shrink-0 items-center gap-1.5">
        <component :is="icon" v-if="icon" class="lucide-icon shrink-0" />
        {{ label }}
      </div>
    </slot>
  </ToggleGroupItem>
</template>
